import { Fragment, useEffect, useState } from "react";

export const GoogleMaps = ({
    latitude,
    longitude,
    onPolygonChange,
    withPolygon,
    resetPolygon,
    id
}: {
    latitude: number;
    longitude: number;
    onPolygonChange?: (area: number, polygon: google.maps.Polygon) => void;
    withPolygon?: boolean;
    resetPolygon?: boolean;
    id?: string;
}) => {
    // Constants
    const mapId = id ?? 'google-maps';

    // State
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [polygon, setPolygon] = useState<google.maps.Polygon | null>(null);

    // Handlers
    const handlePolygonChanges = () => {
        if (polygon) {
            // Emit polygon change
            onPolygonChange && onPolygonChange(
                (google?.maps as any).geometry.spherical.computeArea(polygon.getPath()), polygon
            );
        }
    }

    useEffect(() => {
        if (polygon) {
            // First calculation
            handlePolygonChanges();

            // Add listeners to the polygon
            (google?.maps as any).event.addListener(polygon.getPath(), 'insert_at', handlePolygonChanges);
            (google?.maps as any).event.addListener(polygon.getPath(), 'set_at', handlePolygonChanges);
        }
    }, [polygon]);

    // Change map center
    useEffect(() => {
        if (!!map) {
            map.setCenter(new google.maps.LatLng(latitude, longitude));
        }
    }, [map, latitude, longitude]);

    // Set map polygon initial sides
    useEffect(() => {
        if (withPolygon && !!map) {
            const defase = 0.0001;

            // Build coords for 4-side polygon
            const coords = [{
                lat: latitude + defase,
                lng: longitude - defase
            }, {
                lat: latitude + defase,
                lng: longitude + defase
            }, {
                lat: latitude - defase,
                lng: longitude + defase
            }];

            if (polygon) {
                // Hide old polygon
                polygon.setVisible(false);
                polygon.setPaths(coords);
                setPolygon(polygon);
            }

            // Create new
            setPolygon(
                new google.maps.Polygon({
                    map,
                    paths: coords,
                    strokeColor: "#008098",
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    fillColor: "#008098",
                    fillOpacity: .7,
                    draggable: true,
                    editable: true,
                    clickable: true
                })
            );
        }
    }, [map, latitude, longitude, resetPolygon]);

    // Global function for mounting the maps
    useEffect(() => {
        // Interval until loaded
        const loadMap = () => {
            if (google?.maps) {
                setMap(new google.maps.Map(document.getElementById(mapId)!, {
                    center: { lat: latitude, lng: longitude },
                    scrollwheel: false,
                    zoom: 19,
                    mapTypeId: google.maps.MapTypeId.SATELLITE,
                    tilt: 0,
                    zoomControl: true,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    rotateControl: false
                }));
            } else {
                setTimeout(loadMap, 500); // Retry untill successful
            }
        }

        setTimeout(loadMap, 500);
    }, []);

    return (<Fragment>
        <div id={mapId} className="google-maps" style={{ height: '100%' }} />
        <div className="google-maps--overlay" />
    </Fragment>);
}